import React, {
  memo, useCallback, useMemo, useState,
} from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import {
  IconButton, Icon, Button,
} from '@picsio/ui';
import {
  Rhomb, RhombFilled, Trash, AddCircleFilled,
} from '@picsio/icons';

import localization from '../../../shared/strings';
import DetailsBlock from '../../DetailsBlock';
import Tooltip from '../../Tooltip';
import ReadOnlyFieldTooltip from '../helpers/ReadOnlyFieldTooltip';
import FormAdd from './FormAdd';
import Placeholder from '../../DetailsBlock/Placeholder';
import s from './styles.module.scss';

const l10n = localization.PRODUCT_INFO;

const Options = ({
  currentValue,
  readOnly,
  showDisabledManagementActions = false,
  disabledManagementTooltip,
  detailsPanelVisibility,
  toggleVisibility,
  blockName,
  blockTitle,
  onCreateOption,
  onCreateOptionValue,
  onDeleteOption,
  onDeleteOptionValue,
}) => {
  const [showNewOptionForm, setShowNewOptionForm] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState(null);

  const isManagementDisabled = readOnly && showDisabledManagementActions;
  const hideManagementActions = readOnly && !showDisabledManagementActions;
  const showManagementActions = !hideManagementActions;
  const managementDisabledTooltip = disabledManagementTooltip || l10n.optionsManageOnMasterTooltip;

  const validateOption = useCallback((name) => {
    if (!name) return false;
    return currentValue.every((option) => option.name.toLowerCase() !== name.toLowerCase());
  }, [currentValue]);

  const handleAddOption = useCallback(({ value, value2 }) => {
    if (validateOption(value) && value2) {
      onCreateOption({ name: value, values: [value2] });
      setShowNewOptionForm(false);
    }
  }, [onCreateOption, validateOption]);

  const handleDeleteOption = useCallback((optionName) => {
    onDeleteOption(optionName);
  }, [onDeleteOption]);

  const handleDeleteOptionValue = useCallback((optionName, value) => () => {
    onDeleteOptionValue(optionName, value);
  }, [onDeleteOptionValue]);

  const validateOptionValue = useCallback((name) => {
    if (!name) return false;
    const option = currentValue.find((currentValueItem) => currentValueItem.name === newOptionValue);
    if (!option) return false;
    return option.values.every((value) => (value?.name ?? value).toLowerCase() !== name.toLowerCase());
  }, [currentValue, newOptionValue]);

  const handleAddOptionValue = useCallback(({ value }) => {
    if (validateOptionValue(value)) {
      onCreateOptionValue({ optionName: newOptionValue, name: value });
      setNewOptionValue(null);
    }
  }, [newOptionValue, onCreateOptionValue, validateOptionValue]);

  const hideNewOptionForm = useCallback(() => {
    setShowNewOptionForm(false);
  },
  [setShowNewOptionForm]);

  const hideNewOptionValueForm = useCallback(() => {
    setNewOptionValue(null);
  },
  [setNewOptionValue]);

  const actions = useMemo(() => {
    const isOpened = !!detailsPanelVisibility[blockName];
    if (!isOpened || !showManagementActions) return [];

    if (isManagementDisabled) {
      return [
        <Tooltip key="Add option" content={managementDisabledTooltip} placement="top">
          <Button color="primary" disabled>
            + Add option
          </Button>
        </Tooltip>,
      ];
    }

    return [
      <Tooltip key="Add option" content={currentValue.length > 2 ? 'You have reached the maximum number of options' : null} placement="top">
        <Button color="primary" disabled={currentValue.length > 2} onClick={() => setShowNewOptionForm(true)}>
          + Add option
        </Button>
      </Tooltip>,
    ];
  }, [
    blockName,
    currentValue.length,
    detailsPanelVisibility,
    disabledManagementTooltip,
    isManagementDisabled,
    managementDisabledTooltip,
    showManagementActions,
  ]);

  const actionsClassName = cn(s.actions, { [s.actionsDisabled]: isManagementDisabled });

  return (
    <DetailsBlock
      dataQa="productOptions"
      detailsPanelVisibility={detailsPanelVisibility}
      toggleVisibility={toggleVisibility}
      blockName={blockName}
      blockTitle={blockTitle}
      actions={actions}
    >
      <If condition={showNewOptionForm && !readOnly}>
        <FormAdd
          isOption
          validate={validateOption}
          placeholder="Type option name"
          placeholder2="Type option value"
          errorMessage={l10n.optionExists}
          onSubmit={handleAddOption}
          onCancel={hideNewOptionForm}
        />
      </If>
      <ReadOnlyFieldTooltip tooltip={readOnly && disabledManagementTooltip && !showDisabledManagementActions ? disabledManagementTooltip : null}>
        <Choose>
          <When condition={!currentValue.length}>
            <Placeholder>
              No options available.
            </Placeholder>
          </When>
          <Otherwise>
            <ul className={s.options}>
            {currentValue.map((option) => (
              <li key={option.name} className={s.optionItem}>
                <div className={s.optionName}>
                  <div className={s.optionNameTitle}>
                    <Icon>
                      <RhombFilled />
                    </Icon>
                    {option.name}
                  </div>
                  <If condition={showManagementActions}>
                    <div className={actionsClassName}>
                      <Choose>
                        <When condition={isManagementDisabled}>
                          <Tooltip content={managementDisabledTooltip} placement="top">
                            <IconButton disabled>
                              <AddCircleFilled />
                            </IconButton>
                          </Tooltip>
                          <Tooltip content={managementDisabledTooltip} placement="top">
                            <IconButton disabled>
                              <Trash />
                            </IconButton>
                          </Tooltip>
                        </When>
                        <Otherwise>
                          <Tooltip content="Add option value" placement="top">
                            <IconButton
                              componentProps={{ 'aria-label': 'Add option value' }}
                              onClick={() => setNewOptionValue(option.name)}
                            >
                              <AddCircleFilled />
                            </IconButton>
                          </Tooltip>
                          <Tooltip content="Delete option" placement="top">
                            <IconButton
                              componentProps={{ 'aria-label': 'Delete option' }}
                              onClick={() => handleDeleteOption(option.name)}
                            >
                              <Trash />
                            </IconButton>
                          </Tooltip>
                        </Otherwise>
                      </Choose>
                    </div>
                  </If>
                </div>
                <ul className={s.optionValues}>
                  <If condition={newOptionValue === option.name && !readOnly}>
                    <FormAdd
                      validate={validateOptionValue}
                      placeholder="Type option value"
                      errorMessage={l10n.optionValueExists}
                      onSubmit={handleAddOptionValue}
                      onCancel={hideNewOptionValueForm}
                    />
                  </If>
                  {option.values.map((value, index) => {
                    const valueName = value?.name ?? value;
                    const valueKey = value?.id ?? value?.name ?? index;
                    return (
                      <li key={valueKey} className={cn(s.optionValue, { [s.optionValueWithoutVariants]: !valueName })}>
                        <div className={s.optionNameTitle}>
                          <Icon>
                            <Rhomb />
                          </Icon>
                          {valueName}
                          <If condition={!valueName}>
                            <sup>(has no variants)</sup>
                          </If>
                        </div>
                        <If condition={showManagementActions}>
                          <div className={actionsClassName}>
                            <If condition={option.values.length > 1 && valueName}>
                              <Choose>
                                <When condition={isManagementDisabled}>
                                  <Tooltip content={managementDisabledTooltip} placement="top">
                                    <IconButton disabled>
                                      <Trash />
                                    </IconButton>
                                  </Tooltip>
                                </When>
                                <Otherwise>
                                  <Tooltip content="Delete option value" placement="top">
                                    <IconButton
                                      componentProps={{ 'aria-label': 'Delete option value' }}
                                      onClick={handleDeleteOptionValue(option.name, valueName)}
                                    >
                                      <Trash />
                                    </IconButton>
                                  </Tooltip>
                                </Otherwise>
                              </Choose>
                            </If>
                          </div>
                        </If>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </Otherwise>
      </Choose>
      </ReadOnlyFieldTooltip>

    </DetailsBlock>
  );
};

const optionValueShape = PropTypes.oneOfType([
  PropTypes.string,
  PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
]);

const optionShape = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  values: PropTypes.arrayOf(optionValueShape),
});

Options.propTypes = {
  currentValue: PropTypes.arrayOf(optionShape).isRequired,
  readOnly: PropTypes.bool,
  showDisabledManagementActions: PropTypes.bool,
  disabledManagementTooltip: PropTypes.string,
  detailsPanelVisibility: PropTypes.objectOf(PropTypes.bool).isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  blockName: PropTypes.string.isRequired,
  blockTitle: PropTypes.node.isRequired,
  onCreateOption: PropTypes.func,
  onCreateOptionValue: PropTypes.func,
  onDeleteOption: PropTypes.func,
  onDeleteOptionValue: PropTypes.func,
};

Options.defaultProps = {
  readOnly: false,
  showDisabledManagementActions: false,
  disabledManagementTooltip: null,
  onCreateOption: () => null,
  onCreateOptionValue: () => null,
  onDeleteOption: () => null,
  onDeleteOptionValue: () => null,
};

export default memo(Options);
